import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App.js';
import { applyStoredTheme } from './lib/theme.js';
import './index.css';

// Before the first paint, so a dark-theme owner never sees a white flash — or,
// as it was, a white app that stayed white until they opened Settings.
applyStoredTheme();

// Installing the app is what makes "share to Dintiko" appear in the Android
// share sheet (PLAN §13.5).
registerSW({ immediate: false });

/**
 * Take a new worker's code, once, when it takes over.
 *
 * `skipWaiting` makes a new worker active immediately, but an already-loaded
 * page keeps running the JavaScript it started with — so an installed PWA that
 * is rarely closed can run a build that is days old. The guard matters: the
 * first worker to control a page also fires this, and reloading then would
 * loop on first visit.
 */
if ('serviceWorker' in navigator) {
  // Read now, not inside the handler. `controllerchange` fires *because* the
  // controller was just set, so `navigator.serviceWorker.controller` is always
  // truthy by the time the handler runs — the guard below never fired, and
  // every first visit reloaded itself the moment the new worker claimed the
  // page. Anything typed before that moment was thrown away with it: the smoke
  // tests filled the login form, the claim landed, and the reload posted two
  // empty fields.
  const wasControlled = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !wasControlled) return;
    reloading = true;
    window.location.reload();
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('No #root element in index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
